import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserStatus } from '../../context/UserStatusContext';
import { generateStudioContent } from '../../services/apiService';
import LoadingOverlay from '../atoms/LoadingOverlay';
import styles from './CreativeStudioModal.module.css';
import ReactMarkdown from 'react-markdown';

const loadingMessages = [
  "Consulting with the persona's style guide...",
  'Synthesizing narrative and personality data...',
  'Contacting the Art Director...',
  "Infusing prompt with the persona's unique voice...",
  'Warming up the creative engine...',
  'Adding final artistic touches...',
  'Bringing your vision to life...',
  'Polishing the final creation...',
];

const CreativeStudioModal = ({ onClose, persona, initialData = {} }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { userStatus, refreshUserStatus } = useUserStatus();

  const [view, setView] = useState('input');
  const [resultData, setResultData] = useState(null);
  const [formData, setFormData] = useState({
    prompt: '',
    outputFormat: 'text',
    tuning: {
      postLength: 'medium',
      includeHashtags: true,
    },
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setView('input');
    setResultData(null);
    setFormData({
      prompt: initialData.prompt || '',
      outputFormat: initialData.outputFormat || 'text',
      tuning: {
        postLength: 'medium',
        includeHashtags: true,
        ...(initialData.tuning || {}),
      },
    });
  }, [initialData]);

  const handleCopyText = () => {
    if (resultData?.textResponse) {
      navigator.clipboard.writeText(resultData.textResponse);
    }
  };

  const handleGenerate = async () => {
    if (!formData.prompt) {
      setError('Please describe what you want to create.');
      return;
    }
    setIsGenerating(true);
    setError(null);
    setView('loading');

    try {
      const accessToken = await getAccessTokenSilently();
      const payload = {
        personaId: persona.personaId || persona.profileId,
        prompt: formData.prompt,
        outputFormat: formData.outputFormat,
        tuning: formData.tuning,
      };

      const result = await generateStudioContent(payload, accessToken);
      await refreshUserStatus();
      setResultData(result);
      setView('results');
    } catch (err) {
      setError(err.message);
      setView('input');
    } finally {
      setIsGenerating(false);
    }
  };

  const getButtonCost = () => {
    let textCost = 0;
    let imageCost = 0;
    if (formData.outputFormat === 'text') textCost = 1;
    if (formData.outputFormat === 'image') imageCost = 1;
    if (formData.outputFormat === 'both') {
      textCost = 1;
      imageCost = 1;
    }

    const parts = [];
    if (textCost > 0) parts.push(`${textCost}⚡️`);
    if (imageCost > 0) parts.push(`${imageCost}🎨`);
    return `| ${parts.join(' + ')}`;
  };

  const renderInputView = () => (
    <>
      <div className={styles.modalHeader}>
        <h3>
          <i className="fa-solid fa-wand-magic-sparkles"></i>
          Welcome to 'Creative Studio' by Ego
          <i className="fa-solid fa-wand-magic-sparkles"></i>
        </h3>
        <p>
          This is where your ideas are transformed. We craft content with the
          unique identity, voice, and perspective of your persona.
        </p>
      </div>
      <div className={styles.formGroup}>
        <label htmlFor="prompt">1. What do you want to make?</label>
        <textarea
          id="prompt"
          rows="5"
          value={formData.prompt}
          onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
          placeholder="e.g., A LinkedIn post about the future of remote work..."
        />
      </div>
      <div className={styles.formGroup}>
        <label>2. Select the Output Format</label>
        <div className={styles.segmentedControl}>
          <button
            onClick={() => setFormData({ ...formData, outputFormat: 'text' })}
            className={formData.outputFormat === 'text' ? styles.active : ''}
          >
            📝 Text Post
          </button>
          <button
            onClick={() => setFormData({ ...formData, outputFormat: 'image' })}
            className={formData.outputFormat === 'image' ? styles.active : ''}
          >
            🖼️ Image Only
          </button>
          <button
            onClick={() => setFormData({ ...formData, outputFormat: 'both' })}
            className={formData.outputFormat === 'both' ? styles.active : ''}
          >
            📦 Text & Image
          </button>
        </div>
      </div>
      <div className={styles.formGroup}>
        <label>3. (Optional) Advanced Tuning</label>
        <div className={styles.tuningGrid}>
          <div className={styles.tuningItem}>
            <label htmlFor="postLength">Post Length</label>
            <select
              id="postLength"
              value={formData.tuning.postLength}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tuning: { ...formData.tuning, postLength: e.target.value },
                })
              }
            >
              <option value="short">Short (~1 paragraph)</option>
              <option value="medium">Medium (~3 paragraphs)</option>
              <option value="long">Long (Article)</option>
            </select>
          </div>
          <div className={styles.tuningItem}>
            <label htmlFor="includeHashtags">Include Hashtags?</label>
            <input
              type="checkbox"
              id="includeHashtags"
              checked={formData.tuning.includeHashtags}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tuning: {
                    ...formData.tuning,
                    includeHashtags: e.target.checked,
                  },
                })
              }
            />
          </div>
        </div>
      </div>
      {error && <p className="errorText">{error}</p>}
      <div className={styles.modalFooter}>
        <div className={styles.creditBalance}>
          <span>⚡️ {userStatus?.creatorCredits?.toLocaleString()}</span>
          <span>
            🎨 {userStatus?.maxHdImages - userStatus?.monthlyHdImagesUsed}
          </span>
        </div>
        <button onClick={() => onClose()} className="action-button secondary">
          Cancel
        </button>
        <button onClick={handleGenerate} className="action-button primary">
          ✨ Generate {getButtonCost()}
        </button>
      </div>
    </>
  );

  const renderResultsView = () => (
    <>
      <div className={styles.modalHeader}>
        <h3>✨ Your Creation is Ready! ✨</h3>
      </div>

      <div className={styles.mainActions}>
        {resultData.textResponse && (
          <button onClick={handleCopyText} className="action-button secondary">
            <i className="fas fa-copy"></i> Copy Text
          </button>
        )}
        {resultData.imageUrl && (
          <a
            href={resultData.imageUrl}
            download={`ego-creation-${Date.now()}.png`}
            target="_blank"
            rel="noopener noreferrer"
            className="action-button secondary"
          >
            <i className="fas fa-download"></i> Download Image
          </a>
        )}
      </div>

      <div className={styles.resultsContainer}>
        {resultData.textResponse && (
          <div className={styles.resultText}>
            <ReactMarkdown>{resultData.textResponse}</ReactMarkdown>
          </div>
        )}
        {resultData.imageUrl && (
          <div className={styles.resultImage}>
            <img src={resultData.imageUrl} alt="Generated content" />
            {resultData.enhancedPrompt && (
              <p className={styles.directorPrompt}>
                <strong>Art Director's Prompt:</strong>{' '}
                {resultData.enhancedPrompt}
              </p>
            )}
          </div>
        )}
      </div>
      <div className={styles.modalFooter}>
        <button
          onClick={() => setView('input')}
          className="action-button secondary"
        >
          ← Back to Editor
        </button>
        <button
          onClick={() => onClose(resultData)}
          className="action-button primary"
        >
          {location.pathname.includes('passport')
            ? 'Close & Go to Chat'
            : 'Add to Chat & Close'}
        </button>
      </div>
    </>
  );

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={() => onClose()}>
          &times;
        </button>
        {view === 'loading' && (
          <LoadingOverlay isLoading={true} messages={loadingMessages} />
        )}
        {view === 'input' && renderInputView()}
        {view === 'results' && resultData && renderResultsView()}
      </div>
    </div>
  );
};

export default CreativeStudioModal;
